import axios from 'axios';

// Use REACT_APP_API_URL which includes /api path
// Fallback to localhost for development
const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// API Client with error handling
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ==================== CALENDAR / AVAILABILITY ====================

export const getAvailableSlots = async (date, consultationType = 'ONLINE', venue = null) => {
  const response = await apiClient.get('/calendar/available-slots', {
    params: { date, consultation_type: consultationType, venue }
  });
  return response.data;
};

export const getGoogleAuthUrl = async () => {
  const response = await apiClient.get('/auth/google/login');
  return response.data;
};

export const getGoogleAuthStatus = async () => {
  const response = await apiClient.get('/auth/google/status');
  return response.data;
};

// ==================== APPOINTMENTS ====================

export const createAppointment = async (appointmentData) => {
  const response = await apiClient.post('/appointments', appointmentData);
  return response.data;
};

export const getAppointment = async (bookingId, token = null) => {
  const params = token ? { token } : {};
  const response = await apiClient.get(`/appointments/${bookingId}`, { params });
  return response.data;
};

export const getPatientAppointments = async (email, token) => {
  const response = await apiClient.get(`/appointments/patient/${encodeURIComponent(email)}`, {
    params: { token }
  });
  return response.data;
};

export const updateAppointmentStatus = async (bookingId, updateData) => {
  const response = await apiClient.patch(`/appointments/${bookingId}/status`, updateData);
  return response.data;
};

// ==================== FILE UPLOADS ====================

export const getPresignedUploadUrl = async (fileName, fileType, appointmentId = null) => {
  const response = await apiClient.post('/upload/presigned-url', {
    file_name: fileName,
    file_type: fileType,
    appointment_id: appointmentId
  });
  return response.data;
};

export const uploadFileToS3 = async (presignedUrl, file) => {
  // Upload directly to S3 using presigned URL
  const response = await axios.put(presignedUrl, file, {
    headers: {
      'Content-Type': file.type
    }
  });
  return response;
};

export const getDownloadUrl = async (s3Key) => {
  const response = await apiClient.get(`/files/${encodeURIComponent(s3Key)}/download-url`);
  return response.data;
};

// ==================== PAYMENTS (RAZORPAY) ====================

export const createRazorpayOrder = async (appointmentId) => {
  const response = await apiClient.post('/payments/create-order', {
    appointment_id: appointmentId
  });
  return response.data;
};

export const verifyRazorpayPayment = async (paymentData) => {
  const response = await apiClient.post('/payments/verify', paymentData);
  return response.data;
};

export const getPaymentStatus = async (orderId) => {
  const response = await apiClient.get(`/payments/status/${orderId}`);
  return response.data;
};

// ==================== DASHBOARD ====================

export const getDashboardData = async (bookingId, token = null) => {
  const params = token ? { token } : {};
  const response = await apiClient.get(`/dashboard/${bookingId}`, { params });
  return response.data;
};

// ==================== CONTACT ====================

export const submitContactForm = async (formData) => {
  const response = await apiClient.post('/contact', formData);
  return response.data;
};

// ==================== ADMIN ====================

export const getAdminAppointments = async (secret) => {
  const response = await apiClient.get('/admin/appointments', { params: { secret } });
  return response.data;
};

export const getAdminReports = async (bookingId, secret) => {
  const response = await apiClient.get(`/admin/appointments/${bookingId}/reports`, { params: { secret } });
  return response.data;
};

// ==================== HEALTH CHECK ====================

export const healthCheck = async () => {
  const response = await apiClient.get('/');
  return response.data;
};

export default apiClient;
